import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import cls from "./AdminGameListPage.module.scss";
import { listAdminGames } from "../../../features/game/gameApi";

const statusClassMap = {
  published: "registrationOpen",
  archived: "completed",
  draft: "draft",
  registration_open: "registrationOpen",
  round_live: "roundLive",
  paused: "paused",
  completed: "completed",
};

const statusLabelMap = {
  published: "Published",
  archived: "Archived",
  draft: "Draft",
  registration_open: "Registration Open",
  round_live: "Round Live",
  paused: "Paused",
  completed: "Completed",
};

export default function AdminGameListPage() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const data = await listAdminGames();
        if (mounted) setGames(data.games || []);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const total = games.length;
    const registrationOpen = games.filter(
      (g) => g.status === "registration_open" || g.status === "published",
    ).length;
    const roundLive = games.filter((g) => g.status === "round_live").length;
    const paused = games.filter((g) => g.status === "paused").length;
    const completed = games.filter(
      (g) => g.status === "completed" || g.status === "archived",
    ).length;

    return { total, registrationOpen, roundLive, paused, completed };
  }, [games]);

  return (
    <div className={cls.page}>
      <div className={cls.bgGlowPink} />
      <div className={cls.bgGlowBlue} />

      <div className={cls.container}>
        <header className={cls.header}>
          <div className={cls.kicker}>NOTY ADMIN</div>
          <h1 className={cls.title}>Game Library</h1>
          <p className={cls.subtitle}>
            View and manage all created game rooms inside the Noty Universe
            system.
          </p>
        </header>

        <section className={cls.statGrid}>
          <div className={cls.statCard}>
            <div className={cls.statLabel}>Total Games</div>
            <div className={cls.statValue}>{stats.total}</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statLabel}>Registration Open</div>
            <div className={cls.statValue}>{stats.registrationOpen}</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statLabel}>Round Live</div>
            <div className={cls.statValue}>{stats.roundLive}</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statLabel}>Paused</div>
            <div className={cls.statValue}>{stats.paused}</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statLabel}>Completed</div>
            <div className={cls.statValue}>{stats.completed}</div>
          </div>
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <h2 className={cls.panelTitle}>All Games</h2>
              <p className={cls.panelSub}>
                Secure join rooms, statuses, and direct management access.
              </p>
            </div>
          </div>

          {loading ? (
            <div className={cls.stateCard}>
              <div className={cls.loader} />
              <p>Loading games...</p>
            </div>
          ) : games.length === 0 ? (
            <div className={cls.stateCard}>
              <div className={cls.emptyIcon}>◌</div>
              <h3>No games yet</h3>
              <p>Create your first game room to see it listed here.</p>
            </div>
          ) : (
            <>
              <div className={cls.desktopTableWrap}>
                <table className={cls.table}>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Join Name</th>
                      <th>Status</th>
                      <th>Round</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {games.map((game) => {
                      const statusKey = statusClassMap[game.status] || "draft";

                      return (
                        <tr key={game._id}>
                          <td>
                            <div className={cls.gameTitleWrap}>
                              <span className={cls.gameDot} />
                              <span className={cls.gameTitle}>
                                {game.title}
                              </span>
                            </div>
                          </td>

                          <td>
                            <span className={cls.joinName}>
                              {game.joinName}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`${cls.statusBadge} ${cls[statusKey]}`}
                            >
                              {statusLabelMap[game.status] || game.status}
                            </span>
                          </td>

                          <td>
                            {game.currentRound || 1}/{game.maxRounds || 1}
                          </td>

                          <td>
                            <Link
                              className={cls.manageBtn}
                              to={`/admin/games/${game._id}`}
                            >
                              Manage Game
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className={cls.mobileList}>
                {games.map((game) => {
                  const statusKey = statusClassMap[game.status] || "draft";

                  return (
                    <article className={cls.gameCard} key={game._id}>
                      <div className={cls.gameCardTop}>
                        <div className={cls.gameCardTitleWrap}>
                          <span className={cls.gameDot} />
                          <h3 className={cls.gameCardTitle}>{game.title}</h3>
                        </div>

                        <span
                          className={`${cls.statusBadge} ${cls[statusKey]}`}
                        >
                          {statusLabelMap[game.status] || game.status}
                        </span>
                      </div>

                      <div className={cls.metaGrid}>
                        <div className={cls.metaItem}>
                          <span className={cls.metaLabel}>Join Name</span>
                          <span className={cls.metaValue}>{game.joinName}</span>
                        </div>

                        <div className={cls.metaItem}>
                          <span className={cls.metaLabel}>Round</span>
                          <span className={cls.metaValue}>
                            {game.currentRound || 1}/{game.maxRounds || 1}
                          </span>
                        </div>
                      </div>

                      <Link
                        className={cls.manageBtn}
                        to={`/admin/games/${game._id}`}
                      >
                        Manage Game
                      </Link>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
